// Planning de production : quantités prévues par jour et par type, comparées au réalisé des rapports journaliers.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, FileDownloadOutlined, SaveOutlined, TodayOutlined, UploadFileOutlined } from '@mui/icons-material';
import { Panel } from '../../components/ui';
import { planningAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { tokens } from '../../theme';
import { aujourdhui, jourCourt, lundiDe, plusJours, semaineLabel } from './common';
import ImportDialog from './ImportDialog';
import { ecrireExcel, enDate, enEntier, norme } from '../../utils/excel';

const cleType = (v) => norme(v).replace(/[\s-]/g, '').toUpperCase();
const dateFr = (d) => d.split('-').reverse().join('/');
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const ressembleType = (v) => /^\d{1,2}(AR|B)\d{2,4}$/.test(cleType(v));

/**
 * Lit un planning de production Excel, sur un ou plusieurs onglets. Trois présentations :
 *  - dates en lignes et types en colonnes (onglets « PREVISION … » : DATES | JOURS | TYPE DE JOUR | 9 AR 150 | …) ;
 *  - grille du modèle ERP (types en lignes, dates en colonnes) ;
 *  - liste (colonnes Date, Type, Quantité).
 */
export function analyserProduction(feuilles, codes) {
  const parCle = Object.fromEntries(codes.map((c) => [cleType(c), c]));
  const erreurs = []; const avertissements = new Set(); const valeurs = new Map(); // "date|type" → quantité
  const plusieurs = feuilles.length > 1;
  const poser = (date, type, q, ou) => {
    const k = `${date}|${type}`;
    if (valeurs.has(k) && valeurs.get(k) !== q) avertissements.add(`${type} le ${dateFr(date)} : plusieurs valeurs, la dernière (${q}) est retenue (${ou})`);
    valeurs.set(k, q);
  };
  feuilles.forEach(({ feuille, lignes: rows }) => {
    const ou = (n) => (plusieurs ? `${feuille}, ligne ${n}` : `Ligne ${n}`);
    // 1. Dates en lignes, types en colonnes
    const iT = rows.findIndex((r) => r.filter((c) => ressembleType(c)).length >= 2);
    if (iT >= 0) {
      const h = rows[iT];
      const cols = h.map((c, j) => (ressembleType(c) ? j : -1)).filter((j) => j >= 0);
      const bas = rows.slice(iT + 1);
      const iD = h.map((_, j) => j).filter((j) => !cols.includes(j))
        .sort((a, b) => bas.filter((r) => enDate(r[b])).length - bas.filter((r) => enDate(r[a])).length)[0];
      if (iD === undefined || !bas.some((r) => enDate(r[iD]))) { erreurs.push(`${feuille} : colonne des dates introuvable`); return; }
      const inconnus = {};
      bas.forEach((r, i) => {
        const date = enDate(r[iD]);
        if (!date) return; // lignes de total, potentiel moules, indicateurs…
        cols.forEach((j) => {
          const type = parCle[cleType(h[j])]; const q = enEntier(r[j]);
          if (!type) { if (q > 0) inconnus[h[j]] = (inconnus[h[j]] || 0) + q; return; }
          if (Number.isNaN(q)) erreurs.push(`${ou(iT + i + 2)}, ${h[j]} : quantité invalide (${r[j]})`);
          else poser(date, type, q || 0, ou(iT + i + 2));
        });
      });
      h.filter((c, j) => cols.includes(j) && !parCle[cleType(c)]).forEach((c) => {
        if (inconnus[c]) erreurs.push(`${plusieurs ? `${feuille} : ` : ''}colonne « ${c} » : type absent de l'ERP, ${inconnus[c]} poteau(x) non importé(s)`);
        else avertissements.add(`Colonne « ${c} » ignorée (type absent de l'ERP, aucune quantité)`);
      });
      return;
    }
    // 2. Liste Date / Type / Quantité
    const entete = rows.findIndex((r) => r.some((c) => /^date/.test(norme(c))) && r.some((c) => /^type/.test(norme(c))) && r.some((c) => /(quantit|^qte)/.test(norme(c))));
    if (entete >= 0) {
      const h = rows[entete].map(norme);
      const cD = h.findIndex((c) => /^date/.test(c)); const cT = h.findIndex((c) => /^type/.test(c)); const cQ = h.findIndex((c) => /(quantit|^qte)/.test(c));
      rows.slice(entete + 1).forEach((r, i) => {
        const n = entete + i + 2;
        if (r.every((c) => c === null || c === '')) return;
        const date = enDate(r[cD]); const type = parCle[cleType(r[cT])]; const q = enEntier(r[cQ]);
        if (!date) { erreurs.push(`${ou(n)} : date invalide (${r[cD] ?? 'vide'})`); return; }
        if (!type) { erreurs.push(`${ou(n)} : type inconnu (${r[cT] ?? 'vide'})`); return; }
        if (Number.isNaN(q)) { erreurs.push(`${ou(n)} : quantité invalide (${r[cQ]})`); return; }
        poser(date, type, q || 0, ou(n));
      });
      return;
    }
    // 3. Grille du modèle : types en lignes, dates en colonnes
    const iE = rows.findIndex((r) => r.slice(1).some((c) => enDate(c)));
    if (iE < 0) { if (!plusieurs) erreurs.push('Format non reconnu : dates en lignes et types en colonnes, grille du modèle, ou colonnes Date, Type, Quantité'); return; }
    const cols = rows[iE].map((c, j) => (j > 0 ? enDate(c) : null));
    rows.slice(iE + 1).forEach((r, i) => {
      const n = iE + i + 2;
      const brut = r[0];
      if (brut === null || brut === '' || /^total/.test(norme(brut))) return;
      const type = parCle[cleType(brut)];
      if (!type) { erreurs.push(`${ou(n)} : type inconnu (${brut})`); return; }
      cols.forEach((date, j) => {
        if (!date) return;
        const q = enEntier(r[j]);
        if (Number.isNaN(q)) erreurs.push(`${ou(n)}, ${dateFr(date)} : quantité invalide (${r[j]})`);
        else poser(date, type, q || 0, ou(n));
      });
    });
  });
  const lignes = [...valeurs].map(([k, quantite]) => { const [date, pba_type] = k.split('|'); return { date, pba_type, quantite }; });
  const dates = [...new Set(lignes.map((l) => l.date))].sort();
  const types = codes.filter((c) => lignes.some((l) => l.pba_type === c));
  const total = lignes.reduce((t, l) => t + l.quantite, 0);
  // Aperçu par jour (≤ 14 jours) ou par mois
  const parMois = dates.length > 14;
  const cle = (d) => (parMois ? d.slice(0, 7) : d);
  const groupes = [...new Set(dates.map(cle))];
  const somme = (t, g) => lignes.filter((l) => l.pba_type === t && cle(l.date) === g).reduce((x, l) => x + l.quantite, 0);
  const libelle = (g) => (parMois ? `${MOIS[Number(g.slice(5, 7)) - 1]} ${g.slice(0, 4)}` : dateFr(g));
  return {
    colonnes: ['Type', ...groupes.map(libelle), 'Total'],
    apercu: [...types.map((t) => [t, ...groupes.map((g) => somme(t, g)), groupes.reduce((x, g) => x + somme(t, g), 0)]),
      ...(types.length > 1 ? [['Total', ...groupes.map((g) => types.reduce((x, t) => x + somme(t, g), 0)), total]] : [])],
    erreurs, avertissements: [...avertissements],
    resume: lignes.length ? `${total} poteaux planifiés sur ${dates.filter((d) => lignes.some((l) => l.date === d && l.quantite)).length} jour(s) de production (${dateFr(dates[0])} → ${dateFr(dates[dates.length - 1])}) — les jours du fichier remplacent le planning actuel` : 'Aucune ligne reconnue',
    payload: lignes.length ? lignes : null,
  };
}

const ProductionPlan = ({ onInfo }) => {
  const [debut, setDebut] = useState(lundiDe(aujourdhui()));
  const [d, setD] = useState(null);
  const [saisie, setSaisie] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [imp, setImp] = useState(false);
  const charger = useCallback(() => planningAPI.production(debut).then((r) => {
    setD(r.data);
    setSaisie(Object.fromEntries(r.data.plan.map((p) => [`${p.date}|${p.pba_type}`, String(p.quantite)])));
  }).catch((e) => setError(apiError(e))), [debut]);
  useEffect(() => { charger(); }, [charger]);

  const reel = useMemo(() => Object.fromEntries((d?.realise || []).map((r) => [`${r.date}|${r.pba_type}`, r.quantite])), [d]);
  const initial = useMemo(() => Object.fromEntries((d?.plan || []).map((p) => [`${p.date}|${p.pba_type}`, String(p.quantite)])), [d]);
  const modifs = Object.keys({ ...saisie, ...initial }).filter((k) => (saisie[k] || '') !== (initial[k] || ''));
  const n = (k) => Number(saisie[k] || 0);
  const t = aujourdhui();

  const enregistrer = async () => {
    setBusy(true); setError('');
    try {
      const r = await planningAPI.enregistrerProduction(modifs.map((k) => { const [date, pba_type] = k.split('|'); return { date, pba_type, quantite: saisie[k] || 0 }; }));
      onInfo(r.data.message); charger();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const codes = useMemo(() => (d?.types || []).map((x) => x.code), [d]);
  const analyser = useCallback((rows) => analyserProduction(rows, codes), [codes]);
  const exporter = async () => {
    const lignes = [
      ['Type', ...d.jours.map((j) => ({ value: new Date(`${j}T00:00:00Z`), type: Date, fontWeight: 'bold' }))],
      ...d.types.map((ty) => [ty.code, ...d.jours.map((j) => (n(`${j}|${ty.code}`) || null))]),
    ];
    await ecrireExcel(lignes, `planning-production-${d.debut}.xlsx`, { largeurs: [14, ...d.jours.map(() => 12)] });
  };
  const types = (d?.types || []).filter((ty) => d.modifiable || d.jours.some((j) => n(`${j}|${ty.code}`) || reel[`${j}|${ty.code}`]));
  const totJour = (j, src) => (d?.types || []).reduce((s, ty) => s + (src === 'p' ? n(`${j}|${ty.code}`) : (reel[`${j}|${ty.code}`] || 0)), 0);

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <IconButton aria-label="Semaine précédente" onClick={() => setDebut(plusJours(debut, -7))}><ChevronLeft /></IconButton>
          <Typography sx={{ fontWeight: 700, minWidth: 210, textAlign: 'center' }}>{semaineLabel(debut)}</Typography>
          <IconButton aria-label="Semaine suivante" onClick={() => setDebut(plusJours(debut, 7))}><ChevronRight /></IconButton>
          <Button size="small" startIcon={<TodayOutlined />} onClick={() => setDebut(lundiDe(aujourdhui()))}>Cette semaine</Button>
          <Box sx={{ flex: 1 }} />
          {d && <Button startIcon={<FileDownloadOutlined />} onClick={() => exporter().catch((e) => setError(e.message))}>Excel</Button>}
          {d?.modifiable && <Button startIcon={<UploadFileOutlined />} onClick={() => setImp(true)}>Importer Excel</Button>}
          {d?.modifiable && <Button variant="contained" startIcon={<SaveOutlined />} disabled={busy || !modifs.length} onClick={enregistrer}>Enregistrer{modifs.length ? ` (${modifs.length})` : ''}</Button>}
        </Box>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                {d?.jours.map((j) => <TableCell key={j} align="center" sx={{ whiteSpace: 'nowrap', bgcolor: j === t ? 'action.selected' : undefined }}>{jourCourt(j)}</TableCell>)}
                <TableCell align="right">Semaine</TableCell>
                <Tooltip title="Reste à livrer sur les BC clients ouverts · stock livrable / en maturation"><TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Besoin BC · stock</TableCell></Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((ty) => {
                const p = d.jours.reduce((s, j) => s + n(`${j}|${ty.code}`), 0);
                const r = d.jours.reduce((s, j) => s + (reel[`${j}|${ty.code}`] || 0), 0);
                const b = d.besoins[ty.code];
                return (
                  <TableRow key={ty.code}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{ty.code}</TableCell>
                    {d.jours.map((j) => {
                      const k = `${j}|${ty.code}`; const re = reel[k] || 0; const pl = n(k);
                      return (
                        <TableCell key={j} align="center" sx={{ bgcolor: j === t ? 'action.selected' : undefined }}>
                          {d.modifiable
                            ? <TextField variant="standard" value={saisie[k] || ''} placeholder="—" sx={{ width: 52 }}
                                inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' }, 'aria-label': `${ty.code} ${jourCourt(j)} prévu` }}
                                onChange={(e) => setSaisie((x) => ({ ...x, [k]: e.target.value.replace(/[^\d]/g, '') }))} />
                            : <Typography sx={{ fontFamily: tokens.mono, fontSize: 14 }}>{pl || '—'}</Typography>}
                          {(re > 0 || (pl > 0 && j < t)) && (
                            <Typography sx={{ fontSize: 11.5, fontFamily: tokens.mono, color: re >= pl ? 'success.main' : 'error.main' }}>réel {re}</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{p || '—'}
                      {(p > 0 || r > 0) && <Typography sx={{ fontSize: 11.5, fontFamily: tokens.mono, color: r >= p ? 'success.main' : 'text.secondary' }}>réel {r}{p ? ` (${Math.round((r / p) * 100)} %)` : ''}</Typography>}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, whiteSpace: 'nowrap', color: 'text.secondary' }}>{b.reste_bc || '—'} · {b.livrable}/{b.maturation}</TableCell>
                  </TableRow>
                );
              })}
              {d && (
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell>Total</TableCell>
                  {d.jours.map((j) => <TableCell key={j} align="center" sx={{ fontFamily: tokens.mono }}>{totJour(j, 'p') || '—'}
                    {totJour(j, 'r') > 0 && <Typography sx={{ fontSize: 11.5, fontFamily: tokens.mono, color: 'text.secondary' }}>réel {totJour(j, 'r')}</Typography>}</TableCell>)}
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{d.jours.reduce((s, j) => s + totJour(j, 'p'), 0)}</TableCell><TableCell />
                </TableRow>
              )}
              {d && !types.length && <TableRow><TableCell colSpan={10} sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>Rien de planifié cette semaine.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
        Le réalisé vient des rapports journaliers (vert : objectif atteint, rouge : en dessous). « Besoin BC » = reste à livrer sur les bons de commande clients ouverts ; « stock » = livrable / en maturation.
      </Typography>
      <ImportDialog open={imp} titre="Importer le planning de production (Excel)" onClose={() => setImp(false)}
        consignes={<>Trois présentations sont acceptées : <b>vos onglets de prévision</b> (dates en lignes, types « 9 AR 150 »… en colonnes), la <b>grille du modèle</b> (types en lignes, dates en colonnes) ou une <b>liste</b> Date / Type / Quantité. Une case vide vaut 0. Les onglets reconnus sont présélectionnés ; plusieurs mois peuvent être importés d'un coup. Seuls les jours présents dans le fichier sont modifiés.</>}
        onModele={() => exporter().catch((e) => setError(e.message))} analyser={analyser}
        importer={async (lignes) => (await planningAPI.enregistrerProduction(lignes)).data.message}
        onDone={(m) => { setImp(false); onInfo(`${m} (import Excel)`); charger(); }} />
    </>
  );
};

export default ProductionPlan;
